import useSWR, { mutate } from 'swr';
import { fetcherPost, fetcherWithMethod } from 'utils/axios';
import { handleApiError } from 'utils/errorHandler';
import { useMemo } from 'react';
import { createGenericCRUD } from 'api/commonCrud';
import { PPFProject, PPFProjectFilters, PPFProjectWorkspace, PPFUpdateDescriptionPayload, PPFUpdateSalesRepPayload, PPFUpdateSpecialistPayload, PPFUpdateContactsPayload, PorjectInvoice, ProjectStage, ProjectPaymentSummary } from 'types/PPFUser/project';
import { DuplicateResponse, CrudEndpoints } from 'types/common';
import { createFileManagementHooks } from 'api/fileManagerCommon';
import { AxiosRequestConfig } from 'axios';
import type { MiniDashboardCounts } from 'types/PPFUser/project';

export const endpoints: CrudEndpoints = {
    key: 'Project/PPFProject',
    getList: '/GetList',
    getListById: '/GetList/{ID}',
    getModal: '/',
    getLookupList: '/GetLookupList',
    getListPost: true,
    listPostParams: {
        status: [],
        stageIDs: [],
        taskStatuses: [],
        taskDueDateFrom: null,
        taskDueDateTo: null,
        taskPriorityIDs: [],
        taskAssignedToIDs: [],
        noc: null,
        ntbo: null,
        pp: null,
        coc: null,
        stateID: null,
        countyID: null,
        municipalityIDs: [],
        specialistIDs: [],
        parentCompanyIDs: [],
        contractorCompanyID: null,
    },
    idFieldName: 'projectID',
    insert: '/',
    update: '/',
    delete: ''
};

// Create CRUD operations for PPF Project
const projectCRUD = createGenericCRUD<PPFProject>(endpoints);

export const useCheckDuplicateProjectName = async (projectName: string, excludeID?: number): Promise<boolean> => {
    if (!projectName) return false;
    const endpoint = `${endpoints.key}/CheckDuplicateProjectName?ProjectName=${encodeURIComponent(projectName)}${excludeID ? `&ExcludeID=${excludeID}` : ''}`
    try {
        const res = await fetcherWithMethod('GET', [endpoint, { method: 'GET' }]);
        return res as boolean;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export function useGetProjectsWithFilters(filters: PPFProjectFilters = {}, showAll: boolean = false) {
    const fullEndpoint = `${endpoints.key}${endpoints.getList}`;

    const { data, isLoading, error, isValidating, mutate } = useSWR([fullEndpoint, filters, showAll], ([url, filters, showAll]) => {
        return fetcherWithMethod('POST', [url, { data: filters, params: { showAll } }]);
    },
        {
            revalidateIfStale: true,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            onError: handleApiError
        }
    );

    return useMemo(
        () => {
            const projects = Array.isArray(data?.projects) ? data.projects : Array.isArray(data) ? data : [];

            return {
                data: projects,
                miniDashboardCounts: (data?.miniDashboardCounts ?? null) as MiniDashboardCounts | null,
                totalRecordCount: data?.totalCount ?? data?.totalRecordCount ?? data?.miniDashboardCounts?.totalProjectCount,
                isLoading,
                error,
                mutate,
                isValidating,
                isEmpty: !isLoading && projects.length === 0
            };
        },
        [data, error, isLoading, isValidating, mutate]
    );
}

export function useGetProjectWorkspace(projectId: number | string | null) {
    const endpoint = projectId ? `${endpoints.key}/GetProjectWorkspace/${projectId}` : null;

    const { data, isLoading, error, isValidating, mutate } = useSWR<PPFProjectWorkspace>(
        endpoint ? [endpoint, { method: 'GET' }] : null,
        (args: [string, any]) => fetcherWithMethod('GET', args),
        {
            revalidateIfStale: true,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            onError: (err) => handleApiError(err),
            shouldRetryOnError: false,
        }
    );

    return useMemo(
        () => ({
            data: data || null,
            isLoading,
            error,
            mutate,
            isValidating,
            isEmpty: !isLoading && !data
        }),
        [data, error, isLoading, isValidating, mutate]
    );
}
export const useUpdatePPFProjectDescription = async (data: PPFUpdateDescriptionPayload) => {
    try {
        const response = await fetcherWithMethod('PUT', [`${endpoints.key}/UpdateDescription`, { data }]);
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export const useUpdatePPFProjectSalesRep = async (data: PPFUpdateSalesRepPayload) => {
    try {
        const response = await fetcherWithMethod('PUT', [`${endpoints.key}/UpdateSalesRep`, { data }]);
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export const useUpdatePPFProjectSpecialist = async (data: PPFUpdateSpecialistPayload) => {
    try {
        const response = await fetcherWithMethod('PUT', [`${endpoints.key}/UpdateSpecialist`, { data }]);
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export const useUpdatePPFProjectContacts = async (data: any) => {
    try {
        const response = await fetcherWithMethod('PUT', [`${endpoints.key}/UpdateContact`, { data }]);
        await mutate([`${endpoints.key}/GetProjectWorkspace/${data.projectID}`, { method: 'GET' }]);

        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export const useUpdatePPFProject = (projectID: number) => {
    const updateProject = async (data: any) => {
        try {
            const response = await fetcherWithMethod('PUT', [`${endpoints.key}/UpdateInfo`, { data }]);

            await mutate([`${endpoints.key}/GetProjectWorkspace/${projectID}`, { method: 'GET' }]);
            return response;
        } catch (error) {
            handleApiError(error);
            throw error;
        }
    };

    return { updateProject };
};

export const useUpdatePPFProjectCost = async (data: any) => {
    try {
        const response = await fetcherWithMethod('PUT', [`${endpoints.key}/UpdateCost`, { data }]);
        await mutate([`${endpoints.key}/GetProjectWorkspace/${data.projectID}`, { method: 'GET' }]);

        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export const useSendProjectAssignedToSalesRep = (projectID: number) => {
    return fetcherPost(`${endpoints.key}/SendProjectAssignedToSalesRep/${projectID}`);
};

export const useGetValidateMunicipality = async (projectId: number | string | null, newMunicipalityID: number) => {
    try {
        const endpoint = `${endpoints.key}/GetValidateMunicipality?ProjectID=${projectId}&NewMunicipalityID=${newMunicipalityID}`;
        const res = await fetcherWithMethod('GET', endpoint);

        return res;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
}

export function useGetProjectGetInvoiceList(projectId: number | string | null) {
    const endpoint = projectId ? `${endpoints.key}/GetInvoiceList/${projectId}` : null;

    const { data, isLoading, error, isValidating, mutate } = useSWR<PorjectInvoice[]>(
        endpoint ? [endpoint, { method: 'GET' }] : null,
        (args: [string, any]) => fetcherWithMethod('GET', args),
        {
            revalidateIfStale: true,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            onError: handleApiError
        }
    );

    return useMemo(
        () => ({
            data: data || null,
            isLoading,
            error,
            mutate,
            isValidating,
            isEmpty: !isLoading && !data
        }),
        [data, error, isLoading, isValidating, mutate]
    );
}

export function useGetProjectGetStageSummary(projectId: number | string | null, stageIDs?: number[]) {
    const endpoint = projectId ? `${endpoints.key}/GetStageSummary` : '';

    const { data, isLoading, error, isValidating, mutate } = useSWR<ProjectStage[]>(
        projectId ? [endpoint, {
            method: 'POST',
            data: { projectID: projectId, stageIDs: stageIDs || [] },
        },] : null,
        ([url, options]) => {
            return fetcherWithMethod('POST', [url, options as AxiosRequestConfig]);
        },
        {
            revalidateIfStale: true,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            onError: (err) => handleApiError(err),
            shouldRetryOnError: false
        }
    );

    return useMemo(
        () => ({
            data: data || null,
            isLoading,
            error,
            mutate,
            isValidating,
            isEmpty: !isLoading && !data
        }),
        [data, error, isLoading, isValidating, mutate]
    );
}

export const useUpdateProjectStageSummary = async (stageIDs: number[], projectID: number) => {
    const stageRes = await fetcherWithMethod('POST', [
        'Project/PPFProject/GetStageSummary',
        {
            data: {
                projectID: projectID,
                stageIDs: stageIDs || []
            }
        }
    ]);

    mutate(
        (key: any) => typeof key === 'string' ? key.includes('GetStageSummary') : Array.isArray(key) && key[0]?.includes('GetStageSummary'),
        (currentData: any) => {
            if (!Array.isArray(currentData) || !Array.isArray(stageRes)) return stageRes || currentData;
            const resArray = stageRes as any[];
            return currentData.map((item: any) => {
                const match = resArray.find((s: any) => (s.stageID || s.stageId) === (item.stageID || item.stageId));
                return match ? { ...item, ...match } : item;
            });
        },
        { revalidate: false }
    );
}

export const useGetPPFProjectByID = async (projectID: number) => {
    const res = await fetcherWithMethod('POST', [
        'Project/PPFProject/GetList',
        {
            data: {
                projectID: projectID,
                ...endpoints.listPostParams
            }
        }
    ]);

    const projects = res.projects

    mutate(
        (key: any) => typeof key === 'string' ? key.includes('Project/PPFProject/GetList') : Array.isArray(key) && key[0]?.includes('Project/PPFProject/GetList'),
        (currentData: any) => {
            if (!Array.isArray(currentData?.projects) || !Array.isArray(projects)) return projects || currentData;
            const resArray = projects as any[];

            const data = currentData?.projects?.map((item: any) => {
                const match = resArray.find(
                    (p: any) => (p.projectID || p.primaryKeyID) === (item.projectID || item.primaryKeyID)
                );
                return match ? { ...item, ...match } : item;
            });

            return { ...currentData, projects: data }
        },
        { revalidate: false }
    );
}


export function useGetPaymentsForProject(projectID: number | string | null) {
    const endpoint = projectID ? `${endpoints.key}/GetPayments/${projectID}` : null;

    const { data, isLoading, error, isValidating, mutate } = useSWR<ProjectPaymentSummary>(
        endpoint ? [endpoint, { method: 'GET' }] : null,
        (args: [string, any]) => fetcherWithMethod('GET', args),
        {
            revalidateIfStale: true,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            onError: (err) => handleApiError(err)
        }
    );

    return useMemo(
        () => ({
            payments: data?.payments || null,
            summary: data?.summary || null,
            isLoading,
            error,
            mutate,
            isValidating,
            isEmpty: !isLoading && !data?.payments
        }),
        [data, error, isLoading, isValidating, mutate]
    );
}

export const {
    useGetList: useGetPPFProjectList,
    useGetListByID: useGetPPFProjectListByID,
    useGetModal: useGetPPFProjectModal,
    useGetLookupList: useGetPPFProjectLookupList,
    useInsertRecord: useInsertPPFProjectRecord,
    useUpdateRecord: useUpdatePPFProjectRecord,
    useDeleteRecord: useDeletePPFProjectRecord
} = projectCRUD;
